import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { getDayBook } from '@/api/accountsReports';
import { VoucherView } from '../books/VoucherRegisterPage';
import { Check, Loading, PeriodBar, ReportFrame } from './reportParts';
import { amt, num, tdate, td, th, useExcel, useKeyNav, usePeriod } from './reportUtils';

/** Day Book — every voucher in date order: Date · Particulars · Vch Type · Vch No. · Debit · Credit. */
export function DayBookPage() {
  const [p] = usePeriod();
  const [narr, setNarr] = useState(false);
  const [view, setView] = useState<number | null>(null);
  const q = useQuery({ queryKey: ['acc-books', 'reports', 'day-book', p.from, p.to], queryFn: () => getDayBook(p) });
  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
  const acts = useMemo(() => rows.map((r) => () => setView(r.id)), [rows]);
  const { row } = useKeyNav(acts);
  const excel = useExcel();

  return (
    <>
      <PeriodBar onExcel={rows.length ? () => excel('Day_Book', 'Day Book', ['Date', 'Particulars', 'Vch Type', 'Vch No.', 'Debit', 'Credit', 'Narration'],
        rows.map((r) => [r.date, r.status === 'Cancelled' ? `${r.particulars} (Cancelled)` : r.particulars, r.type, r.voucherNo, r.debit || null, r.credit || null, r.narration ?? ''])) : undefined}>
        <Check label="Show narration" checked={narr} onChange={setNarr} />
        <span className="col-span-3 self-end pb-2 text-[11px] text-[var(--text-muted)] sm:col-auto">{rows.length} voucher{rows.length === 1 ? '' : 's'}</span>
      </PeriodBar>
      <Loading q={q}>
        <ReportFrame title="Day Book" right={`${tdate(p.from)} to ${tdate(p.to)}`} note="Click a voucher to open it — or use ↑ ↓ and Enter.">
          <table className="w-full min-w-[640px] text-xs">
            <thead>
              <tr className="border-b border-[var(--border-color)] text-left">
                <th className={th}>Date</th><th className={th}>Particulars</th><th className={th}>Vch Type</th><th className={th}>Vch No.</th>
                <th className={`${th} text-right`}>Debit Amount</th><th className={`${th} text-right`}>Credit Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} {...row(i, r.status === 'Cancelled' ? 'text-[var(--text-muted)]' : '')}>
                  <td className={`${td} whitespace-nowrap`}>{tdate(r.date)}</td>
                  <td className={td}>
                    <span className="font-semibold">{r.particulars}</span>
                    {r.status === 'Cancelled' && <span className="ml-1.5"><Badge tone="danger">Cancelled</Badge></span>}
                    {narr && r.narration && <div className="text-[11px] text-[var(--text-muted)]">{r.narration}</div>}
                  </td>
                  <td className={td}>{r.type}</td>
                  <td className={`${td} whitespace-nowrap font-mono`}>{r.voucherNo}</td>
                  <td className={num}>{amt(r.debit)}</td>
                  <td className={num}>{amt(r.credit)}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className={`${td} py-6 text-center text-[var(--text-muted)]`}>No vouchers between these dates.</td></tr>}
            </tbody>
          </table>
        </ReportFrame>
      </Loading>
      {view != null && <VoucherView id={view} onClose={() => setView(null)} />}
    </>
  );
}
